import React, { FC, useEffect } from 'react';

import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';

import {
  adminActions,
  adminGetCampaign,
  campaignSelector,
  currency,
  fallbackText,
  HelpTypes,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { SkeletonVariants } from '../../../helpers/Constants';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { getDayMonthDateYear } from '../../Reports/helpers';
import { campaignStatusMap } from '../Campaigns/helper';
import withSkeleton from '../../common/WithSkeleton';
import DisplayChip from '../../common/DisplayChip';
import strings from '../../../localization';

const labelSx = { fontSize: 14, fontWeight: 500, color: 'grey.800' };
const valueSx = { color: 'grey.900', wordBreak: 'break-word', fontSize: 14 };

const Row = ({ label, value }: { label: string; value: string | number }) => {
  const SkeletonTypography = withSkeleton({
    successAction: adminActions.getCampaign,
    variant: SkeletonVariants.text,
  })(Typography);

  return (
    <Grid container xs={12} sx={{ mb: 1.5 }}>
      <Grid item xs={6}>
        <Typography sx={labelSx}>
          {label}
          :
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <SkeletonTypography sx={valueSx}>{value || '-'}</SkeletonTypography>
      </Grid>
    </Grid>
  );
};

interface ICampaignDetails {
  campaignId: string | null;
}

const CampaignDetails: FC<ICampaignDetails> = ({ campaignId }) => {
  const dispatch = useAppDispatch();

  const campaign = useAppSelector(campaignSelector);
  const {
    name,
    promoHourlyRate,
    hourlyRateBonus,
    creditShiftCount,
    startDate,
    endDate,
    validityUntil,
    status,
    userType,
  } = campaign ?? {};

  const sDate = getDayMonthDateYear(startDate ?? '');
  const eDate = getDayMonthDateYear(endDate ?? '');
  const vUntil = getDayMonthDateYear(validityUntil ?? '');

  const rate = `${userType}` === HelpTypes.pharmacy ? promoHourlyRate : hourlyRateBonus;

  const { color, label: statusLabel } = campaignStatusMap[status] ?? {};

  useEffect(() => {
    dispatch(adminGetCampaign(campaignId ?? ''));
  }, []);

  const SkeletonGrid = withSkeleton({
    successAction: adminActions.getCampaign,
    variant: SkeletonVariants.rectangular,
  })(Grid);

  return (
    <Box sx={{ px: 3, pb: 3, pt: 2, minWidth: 360 }}>
      <Row label={strings.name} value={name || fallbackText} />
      <Row
        label={`${strings.promoHourlyRate} / ${strings.bonus}`}
        value={`${currency}${rate}` || fallbackText}
      />
      <Row
        label={strings.totalCredits}
        value={creditShiftCount || fallbackText}
      />
      <Row label={strings.campaignStartDate} value={sDate || fallbackText} />
      <Row label={strings.campaignEndDate} value={eDate || fallbackText} />
      <Row label={strings.creditsValidUntil} value={vUntil || fallbackText} />

      <Grid container xs={12} sx={{ mb: 1.5 }}>
        <Grid item xs={6}>
          <Typography sx={labelSx}>{strings.status}</Typography>
        </Grid>
        <SkeletonGrid xs={6} item>
          <DisplayChip style={{ color, paddingX: 0 }} value={statusLabel} />
        </SkeletonGrid>
      </Grid>
    </Box>
  );
};
export default CampaignDetails;
